import { useCallback, useState } from 'react';
import { APPROVAL_V2_URL } from '@/constant/approval';
import type {
  CreateApprovalRequest,
  CreateApprovalResponse,
} from '@/types/approval';
import { putDataFetcher } from '@/utils/apis';

export type UseUpdateApprovalOptions = {
  onSuccess?: (response: CreateApprovalResponse) => void;
  onError?: (error: unknown) => void;
};

/**
 * 품의 편집 저장 mutation.
 * - PUT /api/v2/approval/{documentId}
 * - 본문 스키마는 POST 와 동일 (status DRAFT/SUBMIT)
 * - 요청에 없는 기존 첨부 fileId 는 서버에서 삭제 처리됨 — 호출부가 유지할
 *   기존 첨부를 반드시 페이로드에 포함시킬 책임
 * - 응답 코드:
 *   200 수정 성공
 *   403 기안자가 아님
 *   404 문서를 찾을 수 없음
 *   422 유효성 실패 / 임시저장 외 상태
 *   에러 메시지 토스트는 호출부 책임 — 본 훅은 error 를 그대로 throw
 */
const useUpdateApproval = (options?: UseUpdateApprovalOptions) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const update = useCallback(
    async (
      documentId: string,
      payload: CreateApprovalRequest,
    ): Promise<CreateApprovalResponse> => {
      setIsSubmitting(true);
      try {
        const response = await putDataFetcher<
          Record<string, unknown>,
          Record<string, unknown>
        >(
          `${APPROVAL_V2_URL}/${documentId}`,
          payload as Record<string, unknown>,
        );
        options?.onSuccess?.(response);
        return response as CreateApprovalResponse;
      } catch (error) {
        options?.onError?.(error);
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [options],
  );

  return { update, isSubmitting };
};

export default useUpdateApproval;
